import { storageKindLabel } from "@/lib/storage-kind-label";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/shared/DataTable";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { useStorageList } from "./hooks/useStorages";
import {
  resolveStorageActionCode,
  resolveStorageAvailabilityCode,
  resolveStorageRuntimeStatusLineCode,
  type StorageAvailabilityCode,
  type StorageSettingDisplayState,
} from "./storage-ux";
import { StorageConnectionKindIconBadge } from "./StorageConnectionKindIcon";
import { HardDrive, Plus } from "lucide-react";
import type { StorageSummary } from "@/types/api";

const AVAILABILITY_BADGE_STATE: Record<StorageAvailabilityCode, string> = {
  available: "ready",
  setup_incomplete: "pending",
  needs_action: "blocked",
  unavailable: "unavailable",
  needs_check: "degraded",
};

const STORAGE_AVAILABILITY_FILTERS: StorageAvailabilityCode[] = [
  "available",
  "setup_incomplete",
  "needs_action",
  "unavailable",
  "needs_check",
];

function isStorageAvailabilityFilter(
  value: string | null,
): value is StorageAvailabilityCode {
  return (
    typeof value === "string" &&
    STORAGE_AVAILABILITY_FILTERS.includes(value as StorageAvailabilityCode)
  );
}

function formatUpdatedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleDateString("ja-JP");
}

function resolveStorageListStatus(storage: StorageSummary) {
  if (storage.storageLifecycleState !== "active") {
    return {
      state: "disabled" as const,
      actionCode: "unavailable",
      showActionLine: true,
    };
  }
  const actionCode = resolveStorageActionCode(storage);
  return {
    state: storage.settingState as StorageSettingDisplayState,
    actionCode,
    showActionLine: actionCode !== "ready",
  };
}

export function StorageListPage() {
  const { t } = useTranslation("storages");
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const availabilityFilterQuery = searchParams.get("availability");
  const availabilityFilter = isStorageAvailabilityFilter(availabilityFilterQuery)
    ? availabilityFilterQuery
    : null;
  const { items, isLoading, hasNext, hasPrev, goNext, goPrev } =
    useStorageList(availabilityFilter ? { limit: "100" } : undefined);
  const filteredItems = availabilityFilter
    ? items.filter((storage) => {
        const status = resolveStorageListStatus(storage);
        const availabilityCode = resolveStorageAvailabilityCode(
          status.state,
          resolveStorageRuntimeStatusLineCode(storage),
        );
        return availabilityCode === availabilityFilter;
      })
    : items;

  function clearFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete("availability");
    setSearchParams(next);
  }

  const columns = [
    {
      key: "displayName",
      header: t("columns.displayName"),
      render: (s: StorageSummary) => (
        <div className="flex min-w-0 items-center gap-3">
          <StorageConnectionKindIconBadge
            connectionKindId={s.connectionKindId}
            label={storageKindLabel(s.connectionKindId, s.providerFamilyDisplayLabel)}
          />
          <span className="min-w-0 truncate font-medium">{s.displayName}</span>
        </div>
      ),
      className: "w-[58%] min-w-80",
    },
    {
      key: "status",
      header: t("columns.status"),
      render: (s: StorageSummary) => {
        const status = resolveStorageListStatus(s);
        const runtimeStatusLineCode = resolveStorageRuntimeStatusLineCode(s);
        const availabilityCode = resolveStorageAvailabilityCode(
          status.state,
          runtimeStatusLineCode,
        );
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                {t("listStatusLabels.availability")}
              </span>
              <StatusBadge
                state={AVAILABILITY_BADGE_STATE[availabilityCode]}
                label={t(`listAvailabilityValues.${availabilityCode}`)}
              />
            </div>
            <dl className="grid gap-1 text-xs">
              <div className="grid grid-cols-[4.5rem_1fr] gap-2">
                <dt className="text-muted-foreground">
                  {t("listStatusLabels.setting")}
                </dt>
                <dd className="font-medium text-foreground">
                  {t(`settingStatusValues.${status.state}`)}
                </dd>
              </div>
              <div className="grid grid-cols-[4.5rem_1fr] gap-2">
                <dt className="text-muted-foreground">
                  {t("listStatusLabels.runtime")}
                </dt>
                <dd className="font-medium text-foreground">
                  {t(`runtimeStatusValues.${runtimeStatusLineCode}`)}
                </dd>
              </div>
            </dl>
            {status.showActionLine ? (
              <p className="text-xs text-muted-foreground">
                {t(`listActionLines.${status.actionCode}`)}
              </p>
            ) : null}
          </div>
        );
      },
      className: "w-72 min-w-72 align-top",
    },
    {
      key: "updatedAt",
      header: t("columns.updatedAt"),
      render: (s: StorageSummary) => formatUpdatedAt(s.updatedAt),
      className: "w-36 min-w-36 whitespace-nowrap text-muted-foreground",
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("title")}</h1>
        <Button onClick={() => navigate("/console/storages/new")} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          {t("newStorage")}
        </Button>
      </div>

      {location.state?.storageDeleted === true ? (
        <div role="status" className="rounded-md border bg-muted/30 p-4 text-sm space-y-2">
          <p>{t("delete.completed")}</p>
          {location.state?.gatewayCleanupNeeded === true ? <p>{t("delete.localGatewayRoutesDeleteScope")}</p> : null}
        </div>
      ) : null}

      {availabilityFilter ? (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <span className="font-semibold">{t("filters.activeTitle")}</span>
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900">
            {t(`filters.values.availability.${availabilityFilter}`)}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-amber-950 hover:bg-amber-100"
            onClick={clearFilters}
          >
            {t("filters.clear")}
          </Button>
        </div>
      ) : null}

      {!isLoading && availabilityFilter && filteredItems.length === 0 ? (
        <div className="flex min-h-72 flex-col items-center justify-center rounded-md border border-dashed bg-muted/20 p-8 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 text-primary">
            <HardDrive className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-semibold">{t("filters.empty")}</h2>
          <Button
            className="mt-5"
            variant="outline"
            size="sm"
            onClick={clearFilters}
          >
            {t("filters.clear")}
          </Button>
        </div>
      ) : !isLoading && items.length === 0 ? (
        <div className="flex min-h-72 flex-col items-center justify-center rounded-md border border-amber-300 bg-amber-50 text-amber-950 p-8 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-md bg-amber-100 text-amber-900">
            <HardDrive className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-semibold">{t("empty.title")}</h2>
          <p className="mt-2 max-w-lg text-sm text-amber-900">
            {t("empty.body")}
          </p>
          <Button
            className="mt-5 bg-amber-300 text-amber-950 hover:bg-amber-400"
            size="lg"
            onClick={() => navigate("/console/storages/new")}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("empty.cta")}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          items={filteredItems}
          isLoading={isLoading}
          hasNext={hasNext}
          hasPrev={hasPrev}
          onNext={goNext}
          onPrev={goPrev}
          onRowClick={(s) => navigate(`/console/storages/${s.storageId}`)}
          keyExtractor={(s) => s.storageId}
          tableClassName="table-fixed min-w-[760px]"
        />
      )}
    </div>
  );
}
